import React, { useEffect, useState } from "react";
import {
  Container, Typography, List, ListItem, ListItemText, Chip, Box, Button,
  CircularProgress, Tabs, Tab
} from "@mui/material";
import api from "../../services/api";
import { buildRecipePath } from "../../utils/recipeUrl";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const RecipesUser = () => {
  const { t, i18n } = useTranslation();
  const [tab, setTab] = useState(0);
  const [recipes, setRecipes] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const params = useParams();
  const [showAllFavorites, setShowAllFavorites] = useState(false);

  // ✅ στο Router σου είναι :country
  let lang = params.country;
  if (!lang) {
    lang = i18n.language === "el" ? "el" : (i18n.language || "en");
  }

  useEffect(() => {
    const fetchRecipes = async () => {
      setLoading(true);
      try {
        const res = await api.recipes.myList(lang);
        setRecipes(res.data.data || []);
      } catch {
        setRecipes([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRecipes();
  }, [lang]);

  useEffect(() => {
    if (tab !== 1) return;

    setLoading(true);
    api.favorites.list({ params: { lang } })
      .then(res => setFavorites(res.data.data || []))
      .catch(() => setFavorites([]))
      .finally(() => setLoading(false));
  }, [tab, lang]);

  const openPublicRecipe = (recipe) => {
    if (!recipe?.slug) return;
    navigate(buildRecipePath(recipe, lang));
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t("my_recipes_title", "Οι συνταγές μου")}
      </Typography>

      <Box sx={{ mb: 2 }}>
        {/* ✅ σωστό route με lang */}
        <Button variant="contained" onClick={() => navigate(`/${lang}/client/add-recipe`)}>
          {t("add_new_recipe", "Προσθήκη νέας συνταγής")}
        </Button>
      </Box>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t("my_recipes", "Οι συνταγές μου")} />
        <Tab label={t("favorites.favorites", "Αγαπημένες συνταγές")} />
      </Tabs>

      {loading ? <CircularProgress /> :
        tab === 0 ? (
          <List>
            {recipes.length === 0 ? (
              <Typography>{t("no_recipes", "Δεν υπάρχουν συνταγές.")}</Typography>
            ) : (
              recipes.map(recipe => (
                <ListItem
                  key={recipe.id}
                  secondaryAction={
                    <Chip
                      label={recipe.visibility === "public" ? t("public", "Δημόσια") : t("private", "Ιδιωτική")}
                      color={recipe.visibility === "public" ? "success" : "default"}
                    />
                  }
                  button
                  onClick={() => navigate(`/${lang}/client/update-recipe/${recipe.slug}`)} // ✅ by slug
                >
                  <ListItemText
                    primary={recipe.name}
                    secondary={(recipe.instructions || "").slice(0, 80) + "..."}
                  />
                </ListItem>
              ))
            )}
          </List>
        ) : (
          <List>
            {favorites.length === 0 ? (
              <Typography>{t("no_favorites", "Δεν υπάρχουν αγαπημένες συνταγές.")}</Typography>
            ) : (
              (showAllFavorites ? favorites : favorites.slice(0, 5)).map(recipe => (
                <ListItem
                  key={recipe.id}
                  button
                  onClick={() => openPublicRecipe(recipe)}
                  sx={{ cursor: "pointer" }}
                  secondaryAction={
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={(e) => {
                        e.stopPropagation();
                        openPublicRecipe(recipe);
                      }}
                      sx={{
                        textTransform: "none",
                        borderRadius: 999,
                        px: 2,
                      }}
                    >
                      {t("read_more", "Δείτε περισσότερα")}
                    </Button>
                  }
                >
                  <ListItemText
                    primary={recipe.name}
                    secondary={(recipe.instructions || "").slice(0, 90) + ((recipe.instructions || "").length > 90 ? "..." : "")}
                  />
                </ListItem>

              ))
            )}
            {!showAllFavorites && favorites.length > 5 && (
              <Box sx={{ textAlign: "center", mt: 2 }}>
                <Button variant="outlined" onClick={() => setShowAllFavorites(true)}>
                  {t("show_more", "Δείτε περισσότερα")}
                </Button>
              </Box>
            )}
          </List>
        )
      }
    </Container>
  );
};

export default RecipesUser;
